import axios from 'axios';
import { api } from '../../../services/api';
import type { SpeciesQuizPair } from '../lib/parseSpeciesQuizCsv';
import type { ShelterAnimal } from '../types';
import type { CompletedUserMatchProfile } from '../lib/userMatchProfile';

export type MatchApiSuccessData = {
  schema: 'matching-poc.response.v2';
  generatedAt: string;
  speciesFilter: 'Cat' | 'Dog';
  questionSource: string;
  preferences: Record<
    string,
    { detailLevel: number; importance: string; weight: number }
  >;
  maxWeightedPenalty: number;
  matches: Array<{
    rank: number;
    matchPercent: number;
    weightedPenalty: number;
    animal: ShelterAnimal;
  }>;
  meta: {
    matchCount: number;
    scoring: string;
    shelterDataSource?: string;
    /** Present when `POST /match` was scoped with `shelterId` or slug body field. */
    matchShelterId?: string;
    matchShelterSlug?: string;
  };
  /** Present when the server persisted this run to `match-results` (public GET). */
  matchResultsId?: string;
};

/** Public GET `/match/:matchResultsId` — same match fields as POST without `preferences`. */
export type MatchResultsPublicData = {
  matchResultsId: string;
  createdAt: string;
  schema: 'matching-poc.response.v2';
  generatedAt: string;
  speciesFilter: 'Cat' | 'Dog';
  questionSource: string;
  maxWeightedPenalty: number;
  matches: MatchApiSuccessData['matches'];
  meta: MatchApiSuccessData['meta'];
};

type ApiSuccess<T> = {
  success: true;
  data: T;
  timestamp: string;
};

export type MatchQuestionsApiData = {
  schema: 'matching-poc.questions.v1';
  species: 'Cat' | 'Dog';
  pairs: SpeciesQuizPair[];
  requestedLocale: string | null;
  resolvedLocale: 'en' | 'es';
  fallbackLocale: 'en';
  fallbackPairIndexes: number[];
  source: { bundle: string };
};

export type MatchRequestBody = {
  species: 'Cat' | 'Dog';
  /** When set, server ranks only that shelter's available animals from Dynamo. */
  shelterId?: string;
  /** Resolved on the server via `settings.adoptionQuiz.slug` (mutually exclusive with `shelterId`). */
  shelterSlug?: string;
  limit?: number;
  profile: CompletedUserMatchProfile;
};

export type MyMatchHistoryRow = {
  createdAt: string;
  species: 'Cat' | 'Dog';
  matchCount: number;
  topMatchAnimalId: string | null;
  topMatchPercent: number | null;
  shelterId: string | null;
  source: string;
};

export type MyMatchHistoryData = {
  period: '7d' | '30d' | '90d' | 'all';
  totalReturned: number;
  history: MyMatchHistoryRow[];
};

export type MyMatchHistorySummaryData = {
  period: '7d' | '30d' | '90d' | 'all';
  totalRuns: number;
  bySpecies: Record<string, number>;
  lastRunAt: string | null;
  avgTopMatchPercent: number | null;
};

export async function getMatchQuestions(
  species: 'Cat' | 'Dog',
  locale: 'en' | 'es',
): Promise<MatchQuestionsApiData> {
  const { data } = await api.get<ApiSuccess<MatchQuestionsApiData>>(
    '/match/questions',
    {
      params: { species },
      headers: { 'Accept-Language': locale },
    },
  );
  return data.data;
}

export async function postMatch(
  body: MatchRequestBody,
): Promise<MatchApiSuccessData> {
  const { data } = await api.post<ApiSuccess<MatchApiSuccessData>>(
    '/match',
    body,
  );
  return data.data;
}

export type ShareMatchResultsEmailRequest = {
  matchResultsId: string;
  recipientEmail: string;
  recipientName?: string;
};

export type ShareMatchResultsEmailResponse = {
  sent: boolean;
  mode?: string;
};

export async function postShareMatchResultsEmail(
  body: ShareMatchResultsEmailRequest,
): Promise<ShareMatchResultsEmailResponse> {
  const { data } = await api.post<ApiSuccess<ShareMatchResultsEmailResponse>>(
    '/match/share/email',
    body,
  );
  return data.data;
}

export async function getMatchResultsById(
  matchResultsId: string,
): Promise<MatchResultsPublicData> {
  const { data } = await api.get<ApiSuccess<MatchResultsPublicData>>(
    `/match/${encodeURIComponent(matchResultsId)}`,
  );
  return data.data;
}

export async function getMyMatchHistory(params?: {
  period?: '7d' | '30d' | '90d' | 'all';
  limit?: number;
}): Promise<MyMatchHistoryData> {
  const { data } = await api.get<ApiSuccess<MyMatchHistoryData>>(
    '/match/history/me',
    { params },
  );
  return data.data;
}

export async function getMyMatchHistorySummary(params?: {
  period?: '7d' | '30d' | '90d' | 'all';
}): Promise<MyMatchHistorySummaryData> {
  const { data } = await api.get<ApiSuccess<MyMatchHistorySummaryData>>(
    '/match/history/me/summary',
    { params },
  );
  return data.data;
}

export type ShelterTopMatchRow = {
  animalId: string;
  /** Resolved server-side from the animals table; null if the animal is gone. */
  animalName: string | null;
  animalPhoto: string | null;
  species: 'Cat' | 'Dog' | null;
  /** How many quiz runs ranked this animal as the #1 match. */
  runCount: number;
  avgTopMatchPercent: number | null;
  lastMatchedAt: string;
};

export type ShelterTopMatchesData = {
  shelterId: string;
  period: '7d' | '30d' | '90d' | 'all';
  species: 'Cat' | 'Dog' | null;
  totalRuns: number;
  topMatches: ShelterTopMatchRow[];
};

/** Whole-shelter aggregate of most-matched animals (incl. anonymous adopters). */
export async function getShelterTopMatches(params?: {
  period?: '7d' | '30d' | '90d' | 'all';
  species?: 'Cat' | 'Dog';
  /** Super Admin only; shelter staff are scoped to their own shelter. */
  shelterId?: string;
  limit?: number;
}): Promise<ShelterTopMatchesData> {
  const { data } = await api.get<ApiSuccess<ShelterTopMatchesData>>(
    '/match/shelter/top-matches',
    { params },
  );
  return data.data;
}

export function getMatchQuestionsErrorMessage(
  err: unknown,
  fallback = 'Could not load match questions',
): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const d = err.response.data as {
      error?: { message?: string; details?: { message: string }[] };
    };
    if (d.error?.details?.length) {
      return d.error.details.map((x) => x.message).join(' ');
    }
    if (d.error?.message) return d.error.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function getMatchApiErrorMessage(
  err: unknown,
  fallback = 'Could not load matches',
): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const d = err.response.data as {
      error?: { message?: string; details?: { message: string }[] };
    };
    if (d.error?.details?.length) {
      return d.error.details.map((x) => x.message).join(' ');
    }
    if (d.error?.message) return d.error.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
